import React, { useCallback, useMemo } from 'react'
import { FlexAlign, FlexColumn } from 'src/components/Reusable/Flex'
import { TextInput } from 'src/components/Reusable/Inputs/TextInput/TextInput'
import { NewMarkdownEditor } from 'src/components/Reusable/Inputs/NewMarkdownEditor'
import { SelectDropdown } from 'src/components/Reusable/SelectDropdown'
import { SelectItem } from 'src/components/Reusable/SelectDropdown/SelectDropdown.constants'
import {
  createEmptyRequirementDraft,
  createEmptySectionDraft,
} from './FrameworkSectionsEditor.helpers'
import {
  FrameworkSectionsEditorErrors,
  RequirementDraft,
  SectionDraft,
} from './FrameworkSectionsEditor.types'
import {
  AddButtonRow,
  CompactMarkdown,
  EditorContainer,
  FieldColumn,
  FrameworkActionButton,
  NameAndControlsRow,
  RemoveIcon,
  RemoveIconButton,
  RequirementRow,
  RequirementRowHeader,
  RequirementRowTitle,
  RequirementsList,
  SectionCard,
  SectionFieldLabel,
  SectionHeaderRow,
  SectionHeaderTitle,
  SectionsList,
} from './FrameworkSectionsEditor.styles'

const SECTION_NAME_MAX = 255
const SECTION_DESCRIPTION_MAX = 2000
const REQUIREMENT_NAME_MAX = 255
const REQUIREMENT_DESCRIPTION_MAX = 2000

export type FrameworkSectionsEditorProps = {
  sections: SectionDraft[]
  setSections: React.Dispatch<React.SetStateAction<SectionDraft[]>>
  setErrors: React.Dispatch<React.SetStateAction<FrameworkSectionsEditorErrors>>
  controlOptions: SelectItem[]
  errors?: FrameworkSectionsEditorErrors
  disableSectionCrud?: boolean
}

export const FrameworkSectionsEditor = ({
  sections,
  setSections,
  setErrors,
  controlOptions,
  errors,
  disableSectionCrud = false,
}: FrameworkSectionsEditorProps): JSX.Element => {
  const addSection = useCallback(() => {
    setSections((prev) => [...prev, createEmptySectionDraft()])
  }, [setSections])

  const removeSection = useCallback(
    (sectionTempId: string) => {
      setSections((prev) => prev.filter((s) => s.tempId !== sectionTempId))
    },
    [setSections],
  )

  const updateSection = useCallback(
    (sectionTempId: string, patch: Partial<SectionDraft>) => {
      if (patch.name !== undefined) {
        setErrors((prev) => ({
          ...prev,
          sectionNames: { ...prev.sectionNames, [sectionTempId]: '' },
        }))
      }

      setSections((prev) =>
        prev.map((s) => (s.tempId === sectionTempId ? { ...s, ...patch } : s)),
      )
    },
    [setSections, setErrors],
  )

  const addRequirement = useCallback(
    (sectionTempId: string) => {
      setSections((prev) =>
        prev.map((s) =>
          s.tempId === sectionTempId
            ? {
                ...s,
                requirements: [
                  ...s.requirements,
                  createEmptyRequirementDraft(),
                ],
              }
            : s,
        ),
      )
    },
    [setSections],
  )

  const removeRequirement = useCallback(
    (sectionTempId: string, requirementTempId: string) => {
      setSections((prev) =>
        prev.map((s) =>
          s.tempId === sectionTempId
            ? {
                ...s,
                requirements: s.requirements.filter(
                  (r) => r.tempId !== requirementTempId,
                ),
              }
            : s,
        ),
      )
    },
    [setSections],
  )

  const updateRequirement = useCallback(
    (
      sectionTempId: string,
      requirementTempId: string,
      patch: Partial<RequirementDraft>,
    ) => {
      if (patch.name !== undefined) {
        setErrors((prev) => ({
          ...prev,
          requirementNames: {
            ...prev.requirementNames,
            [requirementTempId]: '',
          },
        }))
      }
      setSections((prev) =>
        prev.map((s) =>
          s.tempId === sectionTempId
            ? {
                ...s,
                requirements: s.requirements.map((r) =>
                  r.tempId === requirementTempId ? { ...r, ...patch } : r,
                ),
              }
            : s,
        ),
      )
    },
    [setSections, setErrors],
  )

  return (
    <EditorContainer>
      <SectionsList>
        {sections.map((section, index) => (
          <SectionRenderer
            key={section.tempId}
            index={index}
            section={section}
            controlOptions={controlOptions}
            sectionNameError={errors?.sectionNames?.[section.tempId]}
            requirementNameErrors={errors?.requirementNames}
            onUpdateSection={(patch) => updateSection(section.tempId, patch)}
            onRemoveSection={
              disableSectionCrud
                ? undefined
                : () => removeSection(section.tempId)
            }
            onAddRequirement={() => addRequirement(section.tempId)}
            onUpdateRequirement={(reqId, patch) =>
              updateRequirement(section.tempId, reqId, patch)
            }
            onRemoveRequirement={(reqId) =>
              removeRequirement(section.tempId, reqId)
            }
          />
        ))}
      </SectionsList>
      {!disableSectionCrud && (
        <AddButtonRow>
          <FrameworkActionButton
            type="button"
            onClick={(e) => {
              e.preventDefault()
              addSection()
            }}
          >
            + ADD SECTION
          </FrameworkActionButton>
        </AddButtonRow>
      )}
    </EditorContainer>
  )
}

type SectionRendererProps = {
  index: number
  section: SectionDraft
  controlOptions: SelectItem[]
  sectionNameError?: string
  requirementNameErrors?: Record<string, string>
  onUpdateSection: (patch: Partial<SectionDraft>) => void
  onRemoveSection?: () => void
  onAddRequirement: () => void
  onUpdateRequirement: (
    requirementTempId: string,
    patch: Partial<RequirementDraft>,
  ) => void
  onRemoveRequirement: (requirementTempId: string) => void
}

const SectionRenderer = ({
  index,
  section,
  controlOptions,
  sectionNameError,
  requirementNameErrors,
  onUpdateSection,
  onRemoveSection,
  onAddRequirement,
  onUpdateRequirement,
  onRemoveRequirement,
}: SectionRendererProps): JSX.Element => (
  <SectionCard>
    <SectionHeaderRow>
      <SectionHeaderTitle>Section {index + 1}</SectionHeaderTitle>
      {onRemoveSection && (
        <RemoveIconButton
          type="button"
          aria-label="Remove section"
          onClick={onRemoveSection}
        >
          <RemoveIcon />
        </RemoveIconButton>
      )}
    </SectionHeaderRow>
    <FieldColumn>
      <SectionFieldLabel>Section name</SectionFieldLabel>
      <TextInput
        name={`section-name-${section.tempId}`}
        initVal={section.name}
        placeholder="Enter section name (e.g. 'Access Controls')"
        onChangeCb={(v) =>
          onUpdateSection({ name: v.slice(0, SECTION_NAME_MAX) })
        }
        errorMessage={sectionNameError}
        hasError={!!sectionNameError}
        showErrorMessage={!!sectionNameError}
        allowEmptyVal
      />
    </FieldColumn>
    <FieldColumn>
      <SectionFieldLabel>Details (optional)</SectionFieldLabel>
      <CompactMarkdown>
        <NewMarkdownEditor
          value={section.description}
          onChange={(v) =>
            onUpdateSection({
              description: v.slice(0, SECTION_DESCRIPTION_MAX),
            })
          }
          placeholder="Add details about this section"
        />
      </CompactMarkdown>
    </FieldColumn>
    <RequirementsList>
      {section.requirements.map((requirement, idx) => (
        <RequirementRenderer
          key={requirement.tempId}
          index={idx}
          requirement={requirement}
          controlOptions={controlOptions}
          nameError={requirementNameErrors?.[requirement.tempId]}
          onUpdate={(patch) => onUpdateRequirement(requirement.tempId, patch)}
          onRemove={() => onRemoveRequirement(requirement.tempId)}
        />
      ))}
    </RequirementsList>
    <AddButtonRow>
      <FrameworkActionButton
        type="button"
        onClick={(e) => {
          e.preventDefault()
          onAddRequirement()
        }}
      >
        + ADD REQUIREMENT
      </FrameworkActionButton>
    </AddButtonRow>
  </SectionCard>
)

type RequirementRendererProps = {
  index: number
  requirement: RequirementDraft
  controlOptions: SelectItem[]
  nameError?: string
  onUpdate: (patch: Partial<RequirementDraft>) => void
  onRemove?: () => void
  hideRowHeader?: boolean
}

export const RequirementRenderer = ({
  index,
  requirement,
  controlOptions,
  nameError,
  onUpdate,
  onRemove,
  hideRowHeader = false,
}: RequirementRendererProps): JSX.Element => {
  const selectedControls = useMemo(
    () =>
      controlOptions.filter((c) =>
        requirement.controlIds.includes(String(c.value)),
      ),
    [controlOptions, requirement.controlIds],
  )

  return (
    <RequirementRow>
      {!hideRowHeader && (
        <RequirementRowHeader>
          <RequirementRowTitle>Requirement {index + 1}</RequirementRowTitle>
          {onRemove && (
            <RemoveIconButton
              type="button"
              aria-label="Remove requirement"
              onClick={onRemove}
            >
              <RemoveIcon />
            </RemoveIconButton>
          )}
        </RequirementRowHeader>
      )}
      <NameAndControlsRow>
        <FieldColumn>
          <TextInput
            name={`requirement-name-${requirement.tempId}`}
            initVal={requirement.name}
            label="Requirement name"
            labelmb="0"
            placeholder="Enter requirement name (e.g. 'CC1.2 Segregation of Duties')"
            onChangeCb={(v) =>
              onUpdate({ name: v.slice(0, REQUIREMENT_NAME_MAX) })
            }
            errorMessage={nameError}
            hasError={!!nameError}
            showErrorMessage={!!nameError}
            allowEmptyVal
          />
        </FieldColumn>
        <FieldColumn>
          <SelectDropdown
            key={`${requirement.tempId}-${requirement.controlIds.join(',')}`}
            label="Link controls"
            placeholder="Select controls"
            items={controlOptions}
            defaultValues={selectedControls}
            isMultiSelect
            onSelectCb={(items) =>
              onUpdate({ controlIds: items.map((i) => String(i.value)) })
            }
            hideError
          />
        </FieldColumn>
      </NameAndControlsRow>
      <FlexColumn gap={4} align={FlexAlign.FLEX_START} isFullWidth>
        <SectionFieldLabel>Details (optional)</SectionFieldLabel>
        <CompactMarkdown>
          <NewMarkdownEditor
            value={requirement.description}
            onChange={(v) =>
              onUpdate({
                description: v.slice(0, REQUIREMENT_DESCRIPTION_MAX),
              })
            }
            placeholder="Add details about this requirement"
          />
        </CompactMarkdown>
      </FlexColumn>
    </RequirementRow>
  )
}
